import { useEffect } from 'react';
import { useTour } from '@reactour/tour';

// Elements the user is still allowed to scroll / Tab within while a tour is open:
// the tour popover itself, and interactive MUI popups (dropdowns / autocompletes)
// that render in a portal and may need scrolling or keyboard nav to pick an
// option. Note we do NOT whitelist `.MuiModal-root` — that would also unlock the
// CreateMeetingModal dialog, defeating the purpose.
const ALLOW_WITHIN = [
	'[data-tour-popover]',
	'.MuiPopover-root',
	'.MuiPopper-root',
	'.MuiAutocomplete-popper',
].join(',');

// Keys never needed to enter text — blocked on EVERY step, even inside a field
// (Tab = focus navigation, PageUp/PageDown = page scroll). These are the ones
// that otherwise slip through form-heavy tours because focus sits in an input.
const ALWAYS_BLOCK_KEYS = new Set(['Tab', 'PageUp', 'PageDown']);

// Keys that scroll the page but ARE legitimate caret/typing input inside a field
// (Home/End/arrows move the caret, Space types a space). Blocked only when the
// event doesn't originate in an editable field.
const FIELD_EXEMPT_KEYS = new Set([
	'Home',
	'End',
	'ArrowUp',
	'ArrowDown',
	'ArrowLeft',
	'ArrowRight',
	' ', // Space (and Shift+Space) scroll the page when not in a field.
	'Spacebar', // legacy key name
]);

// True when the event originates in a field where these keys are legitimate
// editing/navigation input (typing, caret movement) — never block those.
const isEditable = (el: Element | null | undefined): boolean => {
	if (!el) return false;
	const node = el as HTMLElement;
	const tag = node.tagName;
	return (
		tag === 'INPUT' ||
		tag === 'TEXTAREA' ||
		tag === 'SELECT' ||
		node.isContentEditable === true
	);
};

// While a tour is open, block user-initiated scrolling (wheel / touch AND the
// keyboard scroll keys) plus Tab focus navigation, so the user can't move the
// page to a section the tour never intended or Tab onto a blocked control behind
// the mask (the mask stops clicks, but not keyboard focus/scroll). reactour still
// positions each step via programmatic scrollIntoView, which isn't a user event
// and so is unaffected by preventDefault here.
//
// Everything is scoped: events originating inside the popover, an open dropdown,
// or an editable field pass through untouched, so interactive steps (typing a
// plate, filling a form, picking from a dropdown) keep working normally.
//
// Rendered under the TourProvider (uses useTour); no router needed.
const TourScrollLock = () => {
	const { isOpen } = useTour();

	useEffect(() => {
		if (!isOpen) return;

		// Let the event through if it originates inside the popover or an open
		// dropdown; otherwise it's fair game to cancel.
		const isAllowed = (target: Element | null | undefined) =>
			!!target?.closest?.(ALLOW_WITHIN);

		const blockScroll = (e: Event) => {
			if (isAllowed(e.target as Element | null)) return;
			e.preventDefault();
		};

		const blockKeys = (e: KeyboardEvent) => {
			const target = e.target as Element | null;
			// Keystrokes inside the popover or an open dropdown are always fine.
			if (isAllowed(target)) return;

			if (ALWAYS_BLOCK_KEYS.has(e.key)) {
				// Tab / PageUp / PageDown: never needed to type, so block even in a
				// field — otherwise they slip through form-heavy tours where focus
				// sits in an input.
				e.preventDefault();
				return;
			}
			if (FIELD_EXEMPT_KEYS.has(e.key) && !isEditable(target)) {
				// Scroll keys that double as caret/typing input: block only when not
				// in a field.
				e.preventDefault();
			}
		};

		// Capture phase + passive:false so preventDefault actually cancels.
		const opts = { passive: false, capture: true } as const;
		window.addEventListener('wheel', blockScroll, opts);
		window.addEventListener('touchmove', blockScroll, opts);
		window.addEventListener('keydown', blockKeys, opts);
		return () => {
			window.removeEventListener('wheel', blockScroll, { capture: true });
			window.removeEventListener('touchmove', blockScroll, { capture: true });
			window.removeEventListener('keydown', blockKeys, { capture: true });
		};
	}, [isOpen]);

	return null;
};

export default TourScrollLock;
